import { CreditCard, MapPin, Package, Phone, Repeat, ShieldCheck, Sparkle, SignOut, UserCircle } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { OrderCancelDialog } from "../components/OrderCancelDialog";
import { CustomerAuthCard } from "../components/customer/CustomerAuthCard";
import { CustomerOrderStatusPill } from "../components/customer/CustomerOrderStatusPill";
import { CustomerOrderTimeline } from "../components/customer/CustomerOrderTimeline";
import { MagneticButton } from "../components/MagneticButton";
import { PageIntro } from "../components/PageIntro";
import { RouteTransition } from "../components/RouteTransition";
import { SectionReveal } from "../components/SectionReveal";
import { Seo } from "../components/Seo";
import { useCart } from "../context/CartContext";
import { useCustomer } from "../context/CustomerContext";
import {
  canCustomerSelfManageOrder,
  formatCustomerCurrency,
  formatCustomerOrderDate,
  getOrderItemsCount
} from "../data/customerData";
import { siteInfo } from "../data/siteData";

type ProfileFormState = {
  name: string;
  phone: string;
  email: string;
};

type AddressFormState = {
  title: string;
  address: string;
  detail: string;
};

export function AccountPage() {
  const navigate = useNavigate();
  const {
    isLoading,
    isAuthenticated,
    profile,
    addresses,
    paymentMethods,
    preferences,
    activeOrder,
    orderHistory,
    stats,
    saveProfile,
    savePreferences,
    upsertAddress,
    setPrimaryAddress,
    setPrimaryPaymentMethod,
    refresh,
    startEditingOrder,
    cancelOrder,
    clearEditingOrder,
    logout
  } = useCustomer();
  const [searchParams] = useSearchParams();
  const { addItem, itemCount, replaceWithItems, clear } = useCart();
  const primaryAddress = addresses.find((item) => item.primary) ?? addresses[0];
  const primaryPayment = paymentMethods.find((item) => item.primary) ?? paymentMethods[0];
  const [profileForm, setProfileForm] = useState<ProfileFormState>({
    name: profile.name,
    phone: profile.phone,
    email: profile.email
  });
  const [addressForm, setAddressForm] = useState<AddressFormState>({
    title: primaryAddress?.title ?? "Дом",
    address: primaryAddress?.address ?? "",
    detail: primaryAddress?.detail ?? ""
  });
  const [profileStatus, setProfileStatus] = useState("");
  const [addressStatus, setAddressStatus] = useState("");
  const [repeatStatus, setRepeatStatus] = useState("");
  const [actionStatus, setActionStatus] = useState("");
  const [actionPending, setActionPending] = useState(false);
  const [actionOrderId, setActionOrderId] = useState("");
  const [logoutPending, setLogoutPending] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const activeOrderItems = useMemo(() => (activeOrder ? getOrderItemsCount(activeOrder) : 0), [activeOrder]);

  useEffect(() => {
    setProfileForm({
      name: profile.name,
      phone: profile.phone,
      email: profile.email
    });
  }, [profile]);

  useEffect(() => {
    if (!primaryAddress) {
      return;
    }

    setAddressForm({
      title: primaryAddress.title,
      address: primaryAddress.address,
      detail: primaryAddress.detail
    });
  }, [primaryAddress]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const paymentState = params.get("payment");
    const orderId = params.get("orderId") ?? "";
    const retryTimers: number[] = [];
    const reloadStorageKey = orderId ? `robokassa-site-refresh:${orderId}` : "robokassa-site-refresh";

    if (!paymentState) {
      return;
    }

    if (paymentState === "success") {
      clear();
      clearEditingOrder();
      setActionStatus(
        orderId
          ? `Оплата по заказу ${orderId} принята. Трек заказа уже открыт в кабинете и будет обновляться автоматически.`
          : "Оплата принята. Трек заказа уже открыт в кабинете и будет обновляться автоматически."
      );
      void refresh();
      [1200, 2600, 5000, 9000].forEach((delay) => {
        retryTimers.push(
          window.setTimeout(() => {
            void refresh();
          }, delay)
        );
      });
      retryTimers.push(
        window.setTimeout(() => {
          if (window.sessionStorage.getItem(reloadStorageKey)) {
            return;
          }

          window.sessionStorage.setItem(reloadStorageKey, "1");
          window.location.reload();
        }, 1800)
      );
    } else if (paymentState === "failed") {
      setActionStatus(
        orderId
          ? `Оплата по заказу ${orderId} не завершена. Можно вернуться в checkout и попробовать еще раз.`
          : "Оплата не завершена. Можно вернуться в checkout и попробовать еще раз."
      );
    }

    params.delete("payment");
    params.delete("orderId");
    const nextQuery = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}`);

    return () => {
      retryTimers.forEach((timerId) => window.clearTimeout(timerId));
    };
  }, [clear, clearEditingOrder, refresh, searchParams]);

  const handleProfileSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      await saveProfile(profileForm);
      setProfileStatus("Профиль обновлен. Следующий заказ подставит эти данные автоматически.");
    } catch (error) {
      setProfileStatus(error instanceof Error ? error.message : "Не удалось обновить профиль.");
    }
  };

  const handleAddressSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const id =
      addressForm.title
        .toLowerCase()
        .replace(/[^a-zа-я0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "") || "saved-address";

    try {
      await upsertAddress({
        id,
        title: addressForm.title,
        address: addressForm.address,
        detail: addressForm.detail,
        eta: "25-45 минут",
        primary: true
      });
      await setPrimaryAddress(id);
      setAddressStatus("Основной адрес сохранен и сразу стал адресом по умолчанию.");
    } catch (error) {
      setAddressStatus(error instanceof Error ? error.message : "Не удалось сохранить адрес.");
    }
  };

  const handleRepeatOrder = (orderId: string) => {
    const order = [activeOrder, ...orderHistory].find((item) => item?.id === orderId);

    if (!order) {
      return;
    }

    order.items.forEach((item) => {
      addItem(item.dishId, {
        removedIngredientIds: item.removedIngredientIds,
        addOnIds: item.addOnIds
      });
    });

    setRepeatStatus(`Заказ ${order.id} снова добавлен в корзину. Сейчас там ${itemCount + getOrderItemsCount(order)} позиций.`);
  };

  const handleEditOrder = (orderId: string) => {
    const order = [activeOrder, ...orderHistory].find((item) => item?.id === orderId);

    if (!order || !canCustomerSelfManageOrder(order)) {
      return;
    }

    startEditingOrder(order.id);
    replaceWithItems(
      order.items.map((item) => ({
        dishId: item.dishId,
        qty: item.qty,
        removedIngredientIds: item.removedIngredientIds,
        addOnIds: item.addOnIds
      }))
    );
    navigate("/order");
  };

  const handleCancelOrder = async (orderId: string) => {
    setActionPending(true);
    setActionOrderId(orderId);
    setActionStatus("");

    try {
      await cancelOrder(orderId);
      setActionStatus(`Заказ ${orderId} отменен. Его можно собрать заново в checkout.`);
    } catch (error) {
      setActionStatus(error instanceof Error ? error.message : "Не удалось отменить заказ.");
    } finally {
      setActionPending(false);
    }
  };

  const handleLogout = async () => {
    setLogoutPending(true);

    try {
      await logout();
    } finally {
      setLogoutPending(false);
    }
  };

  if (isLoading) {
    return (
      <RouteTransition>
        <section className="px-4 pb-12 pt-10 md:pt-16">
          <div className="mx-auto max-w-[1440px] space-y-6">
            <div className="h-5 w-36 animate-pulse rounded-full bg-stone-900/8" />
            <div className="h-20 max-w-3xl animate-pulse rounded-[2rem] bg-stone-900/8" />
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
              <div className="h-[34rem] animate-pulse rounded-[2.5rem] bg-white/70" />
              <div className="h-[28rem] animate-pulse rounded-[2.2rem] bg-white/70" />
            </div>
          </div>
        </section>
      </RouteTransition>
    );
  }

  if (!isAuthenticated) {
    return (
      <RouteTransition>
        <Seo
          title={`Личный кабинет | ${siteInfo.brand}`}
          description="Вход и регистрация в личный кабинет Чайханы Изумруд для адресов, истории заказов и быстрых повторов."
        />

        <section className="px-4 pb-12 pt-10 md:pt-16">
          <div className="mx-auto max-w-[1440px] space-y-10">
            <PageIntro
              eyebrow="Личный кабинет"
              title="Войдите или создайте кабинет, чтобы заказы, адреса и настройки жили в одной системе."
              text="После входа сайт и мобильная версия будут подтягивать сохраненные контакты, адреса доставки, историю и ваши пожелания к кухне без повторного ввода."
            />

            <CustomerAuthCard />

            <div className="grid gap-4 md:grid-cols-3">
              {[
                {
                  title: "История заказов",
                  text: "Повтор любимых позиций в один клик и прозрачный архив по датам."
                },
                {
                  title: "Сохраненные адреса",
                  text: "Дом, офис и отдельные сценарии доставки уже готовы к checkout."
                },
                {
                  title: "Настройки кухни",
                  text: "Без лука, соусы отдельно, halal-пометка и другие пожелания хранятся в профиле."
                }
              ].map((item) => (
                <article
                  key={item.title}
                  className="rounded-[2rem] border border-white/72 bg-white/78 p-5 shadow-[0_24px_58px_-40px_rgba(27,53,52,0.44)] backdrop-blur-xl"
                >
                  <p className="text-[1.1rem] font-semibold tracking-[-0.04em] text-stone-950">{item.title}</p>
                  <p className="mt-3 text-sm leading-7 text-stone-600">{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </RouteTransition>
    );
  }

  return (
    <RouteTransition>
      <Seo
        title={`Личный кабинет | ${siteInfo.brand}`}
        description="Профиль, адреса, способы оплаты, активный заказ и история клиента Чайханы Изумруд."
      />

      <section className="px-4 pb-12 pt-10 md:pt-16">
        <div className="mx-auto max-w-[1440px] space-y-10">
          <PageIntro
            eyebrow="Личный кабинет"
            title="Профиль, адреса и заказы в одном спокойном кабинете."
            text="Здесь можно держать под рукой контакты, основной адрес, сценарии доставки и историю заказов без повторных звонков и путаницы."
            aside={
              <div className="inline-flex items-center rounded-full border border-white/70 bg-white/64 px-4 py-2 text-sm text-stone-600 shadow-[0_18px_44px_-34px_rgba(27,53,52,0.4)]">
                заказов всего: {stats.totalOrders}
              </div>
            }
          />

          {repeatStatus ? (
            <SectionReveal className="rounded-[1.8rem] border border-emerald-500/14 bg-emerald-500/[0.08] px-5 py-4 text-sm leading-7 text-emerald-900">
              {repeatStatus}
            </SectionReveal>
          ) : null}

          <div className="grid gap-8 xl:grid-cols-[minmax(0,1.08fr)_24rem]">
            <div className="space-y-6">
              <SectionReveal className="overflow-hidden rounded-[2.5rem] bg-[linear-gradient(160deg,#134042_0%,#0d2829_100%)] p-6 text-stone-50 shadow-[0_38px_88px_-54px_rgba(12,35,36,0.8)]">
                <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="grid h-18 w-18 place-items-center rounded-[1.8rem] border border-white/12 bg-white/10 text-xl font-semibold">
                      {profile.avatar}
                    </div>
                    <div>
                      <p className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-[0.72rem] uppercase tracking-[0.18em] text-teal-100/90">
                        <Sparkle size={14} weight="fill" />
                        {profile.tier}
                      </p>
                      <h1 className="mt-3 text-[2rem] font-semibold leading-[0.96] tracking-[-0.06em]">
                        {profile.name}
                      </h1>
                      <p className="mt-2 text-sm text-stone-200/82">{profile.phone}</p>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-[1.6rem] border border-white/10 bg-white/10 p-4">
                      <p className="text-[0.72rem] uppercase tracking-[0.18em] text-stone-300">Бонусы</p>
                      <p className="mt-2 text-[1.65rem] font-semibold tracking-[-0.05em]">{profile.bonusBalance}</p>
                    </div>
                    <div className="rounded-[1.6rem] border border-white/10 bg-white/10 p-4">
                      <p className="text-[0.72rem] uppercase tracking-[0.18em] text-stone-300">Основной адрес</p>
                      <p className="mt-2 text-sm font-semibold tracking-tight">{stats.favoriteAddress}</p>
                      <p className="mt-2 text-sm text-stone-300">{stats.lastOrderDate}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid gap-3 md:grid-cols-3">
                  <Link
                    to="/order"
                    className="inline-flex items-center justify-between rounded-[1.5rem] bg-white px-4 py-3 text-sm font-medium text-stone-950 transition duration-300 active:translate-y-px active:scale-[0.98]"
                  >
                    <span>Открыть checkout</span>
                    <Package size={16} />
                  </Link>
                  <Link
                    to="/menu"
                    className="inline-flex items-center justify-between rounded-[1.5rem] border border-white/12 bg-white/10 px-4 py-3 text-sm font-medium text-stone-50 transition duration-300 active:translate-y-px active:scale-[0.98]"
                  >
                    <span>Вернуться в меню</span>
                    <Repeat size={16} />
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    disabled={logoutPending}
                    className="inline-flex items-center justify-between rounded-[1.5rem] border border-white/12 bg-white/10 px-4 py-3 text-sm font-medium text-stone-50 transition duration-300 disabled:opacity-70 active:translate-y-px active:scale-[0.98]"
                  >
                    <span>{logoutPending ? "Выходим..." : "Выйти"}</span>
                    <SignOut size={16} />
                  </button>
                </div>
              </SectionReveal>

              <SectionReveal className="rounded-[2.3rem] border border-white/72 bg-white/78 p-6 shadow-[0_34px_78px_-46px_rgba(27,53,52,0.56)] backdrop-blur-2xl">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm uppercase tracking-[0.18em] text-teal-800">Текущий заказ</p>
                    <h2 className="mt-2 text-[1.7rem] font-semibold tracking-[-0.05em] text-stone-950">
                      {activeOrder ? activeOrder.id : "Сейчас без активной доставки"}
                    </h2>
                  </div>
                  {activeOrder ? <CustomerOrderStatusPill order={activeOrder} /> : null}
                </div>

                {activeOrder ? (
                  <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_18rem]">
                    <div className="space-y-4">
                      <div className="grid gap-3 md:grid-cols-3">
                        <StatCard title="Статус" value={activeOrder.status} />
                        <StatCard title="ETA" value={activeOrder.eta} />
                        <StatCard title="Состав" value={`${activeOrderItems} позиций • ${formatCustomerCurrency(activeOrder.total)}`} />
                      </div>

                      <div className="rounded-[1.8rem] border border-stone-900/8 bg-stone-50/82 p-5">
                        <p className="text-sm font-semibold tracking-tight text-stone-950">
                          {activeOrder.addressTitle} • {activeOrder.address}
                        </p>
                        <p className="mt-2 text-sm leading-7 text-stone-600">{activeOrder.addressDetail}</p>
                        <p className="mt-3 text-sm leading-7 text-stone-500">
                          {activeOrder.paymentLabel} • {activeOrder.timeLabel}
                        </p>
                        {activeOrder.note ? (
                          <p className="mt-3 rounded-[1.3rem] border border-teal-900/10 bg-white px-4 py-3 text-sm leading-7 text-stone-600">
                            {activeOrder.note}
                          </p>
                        ) : null}
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <a href={siteInfo.phoneHref} className="inline-flex items-center gap-2 rounded-full border border-stone-900/10 bg-white px-4 py-2 text-sm font-medium text-stone-700">
                          <Phone size={16} />
                          Связаться
                        </a>
                        {canCustomerSelfManageOrder(activeOrder) ? (
                          <button
                            type="button"
                            onClick={() => handleEditOrder(activeOrder.id)}
                            disabled={actionPending}
                            className="inline-flex items-center gap-2 rounded-full border border-stone-900/10 bg-white px-4 py-2 text-sm font-medium text-stone-700 transition duration-300 disabled:cursor-not-allowed disabled:opacity-60 active:translate-y-px active:scale-[0.98]"
                          >
                            <Package size={16} />
                            Редактировать
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() =>
                            canCustomerSelfManageOrder(activeOrder)
                              ? void handleCancelOrder(activeOrder.id)
                              : setIsCancelDialogOpen(true)
                          }
                          disabled={actionPending}
                          className="inline-flex items-center gap-2 rounded-full border border-rose-900/10 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-800 transition duration-300 disabled:cursor-not-allowed disabled:opacity-60 active:translate-y-px active:scale-[0.98]"
                        >
                          <Phone size={16} />
                          {actionPending && actionOrderId === activeOrder.id ? "Отменяем..." : "Отменить заказ"}
                        </button>
                        <Link
                          to="/order"
                          className="inline-flex items-center gap-2 rounded-full border border-teal-900/10 bg-teal-900/[0.05] px-4 py-2 text-sm font-medium text-teal-900"
                        >
                          <Package size={16} />
                          Открыть checkout
                        </Link>
                      </div>
                      <p className="text-sm leading-7 text-stone-500">
                        {canCustomerSelfManageOrder(activeOrder)
                          ? "Пока заказ не оплачен, его можно самому отредактировать или отменить без звонка."
                          : "Для отмены оплаченного или уже подтвержденного заказа позвоните в чайхану."}
                      </p>
                      {actionStatus ? <p className="text-sm leading-7 text-stone-600">{actionStatus}</p> : null}
                    </div>

                    <div className="rounded-[1.9rem] bg-[linear-gradient(160deg,#134042_0%,#0d2829_100%)] p-5 text-stone-50 shadow-[0_28px_64px_-40px_rgba(12,35,36,0.78)]">
                      <p className="text-[0.72rem] uppercase tracking-[0.18em] text-teal-100/84">Лента заказа</p>
                      <div className="mt-4">
                        <CustomerOrderTimeline steps={activeOrder.steps} dark />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-5 rounded-[1.8rem] border border-dashed border-stone-900/10 bg-stone-50/80 p-6">
                    <p className="text-lg font-medium tracking-tight text-stone-950">Активных заказов сейчас нет.</p>
                    <p className="mt-3 max-w-[54ch] text-sm leading-7 text-stone-600">
                      Можно быстро повторить прошлый ужин из истории ниже или сразу собрать новый заказ через меню.
                    </p>
                    <div className="mt-5">
                      <MagneticButton to="/menu" variant="secondary" size="md">
                        Открыть меню
                      </MagneticButton>
                    </div>
                  </div>
                )}
              </SectionReveal>

              <SectionReveal className="rounded-[2.3rem] border border-white/72 bg-white/78 p-6 shadow-[0_34px_78px_-46px_rgba(27,53,52,0.56)] backdrop-blur-2xl">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm uppercase tracking-[0.18em] text-teal-800">История</p>
                    <h2 className="mt-2 text-[1.6rem] font-semibold tracking-[-0.05em] text-stone-950">
                      Прошлые заказы
                    </h2>
                  </div>
                  <span className="rounded-full border border-teal-900/10 bg-teal-900/[0.05] px-3 py-2 text-sm text-teal-900">
                    {orderHistory.length}
                  </span>
                </div>

                <div className="mt-5 space-y-4">
                  {orderHistory.length === 0 ? (
                    <div className="rounded-[1.8rem] border border-dashed border-stone-900/10 bg-stone-50/80 p-6 text-sm leading-7 text-stone-600">
                      История еще не накопилась. После первого оформленного заказа здесь появятся повтор, даты и состав.
                    </div>
                  ) : (
                    orderHistory.map((order) => {
                      const canSelfManage = canCustomerSelfManageOrder(order);
                      const isCurrentAction = actionOrderId === order.id;

                      return (
                        <article key={order.id} className="rounded-[1.8rem] border border-stone-900/8 bg-stone-50/82 p-5">
                          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                            <div>
                              <p className="text-sm font-semibold tracking-tight text-stone-950">
                                {order.id} • {formatCustomerOrderDate(order.createdAt)}
                              </p>
                              <p className="mt-2 text-sm leading-7 text-stone-600">
                                {order.addressTitle} • {order.address}
                              </p>
                              <p className="text-sm text-stone-500">
                                {order.shortStatus} • {formatCustomerCurrency(order.total)}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {canSelfManage ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleEditOrder(order.id)}
                                    disabled={actionPending}
                                    className="inline-flex items-center gap-2 rounded-full border border-stone-900/10 bg-white px-4 py-2 text-sm font-medium text-stone-700 transition duration-300 disabled:cursor-not-allowed disabled:opacity-60 active:translate-y-px active:scale-[0.98]"
                                  >
                                    Редактировать
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handleCancelOrder(order.id)}
                                    disabled={actionPending}
                                    className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700 transition duration-300 disabled:cursor-not-allowed disabled:opacity-60 active:translate-y-px active:scale-[0.98]"
                                  >
                                    {actionPending && isCurrentAction ? "Отменяем..." : "Отменить"}
                                  </button>
                                </>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => handleRepeatOrder(order.id)}
                                className="inline-flex items-center gap-2 rounded-full border border-stone-900/10 bg-white px-4 py-2 text-sm font-medium text-stone-700 transition duration-300 active:translate-y-px active:scale-[0.98]"
                              >
                                <Repeat size={16} />
                                Повторить
                              </button>
                            </div>
                          </div>

                          <div className="mt-4 flex flex-wrap gap-2">
                            {order.items.map((item) => (
                              <span
                                key={`${order.id}-${item.dishId}`}
                                className="rounded-full border border-teal-900/10 bg-white px-3 py-1.5 text-[0.74rem] text-teal-800"
                              >
                                {item.qty} x {item.name}
                              </span>
                            ))}
                          </div>

                          {isCurrentAction && actionStatus ? (
                            <p className="mt-3 text-sm leading-7 text-stone-600">{actionStatus}</p>
                          ) : null}
                        </article>
                      );
                    })
                  )}
                </div>
              </SectionReveal>
            </div>

            <div className="space-y-6">
              <SectionReveal delay={0.06} className="rounded-[2.2rem] border border-white/72 bg-white/78 p-6 shadow-[0_34px_78px_-46px_rgba(27,53,52,0.56)] backdrop-blur-2xl">
                <div className="flex items-center gap-3">
                  <UserCircle size={18} className="text-teal-700" />
                  <div>
                    <p className="text-sm uppercase tracking-[0.18em] text-teal-800">Контакты</p>
                    <p className="mt-1 text-sm leading-6 text-stone-600">Эти данные автоматически подставляются в checkout.</p>
                  </div>
                </div>

                <form onSubmit={handleProfileSubmit} className="mt-5 grid gap-4">
                  <label className="grid gap-2 text-sm text-stone-600">
                    Имя
                    <input
                      value={profileForm.name}
                      onChange={(event) => setProfileForm((current) => ({ ...current, name: event.target.value }))}
                      className="rounded-[1.25rem] border border-teal-900/10 bg-white/84 px-4 py-3 text-base text-stone-950 outline-none transition duration-300 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/10"
                    />
                  </label>

                  <label className="grid gap-2 text-sm text-stone-600">
                    Телефон
                    <input
                      value={profileForm.phone}
                      onChange={(event) => setProfileForm((current) => ({ ...current, phone: event.target.value }))}
                      className="rounded-[1.25rem] border border-teal-900/10 bg-white/84 px-4 py-3 text-base text-stone-950 outline-none transition duration-300 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/10"
                    />
                  </label>

                  <label className="grid gap-2 text-sm text-stone-600">
                    E-mail (необязательно)
                    <input
                      value={profileForm.email}
                      onChange={(event) => setProfileForm((current) => ({ ...current, email: event.target.value }))}
                      placeholder="Можно оставить пустым"
                      className="rounded-[1.25rem] border border-teal-900/10 bg-white/84 px-4 py-3 text-base text-stone-950 outline-none transition duration-300 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/10"
                    />
                  </label>

                  {profileStatus ? <p className="text-sm leading-7 text-emerald-700">{profileStatus}</p> : null}

                  <MagneticButton type="submit" fullWidth>
                    Сохранить профиль
                  </MagneticButton>
                </form>
              </SectionReveal>

              <SectionReveal delay={0.1} className="rounded-[2.2rem] border border-white/72 bg-white/78 p-6 shadow-[0_34px_78px_-46px_rgba(27,53,52,0.56)] backdrop-blur-2xl">
                <div className="flex items-center gap-3">
                  <MapPin size={18} className="text-teal-700" />
                  <div>
                    <p className="text-sm uppercase tracking-[0.18em] text-teal-800">Основной адрес</p>
                    <p className="mt-1 text-sm leading-6 text-stone-600">Можно менять домашний адрес и держать его адресом по умолчанию.</p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {addresses.map((address) => (
                    <button
                      key={address.id}
                      type="button"
                      onClick={() => void setPrimaryAddress(address.id)}
                      className={[
                        "rounded-full border px-3 py-2 text-sm font-medium transition duration-300 active:scale-[0.98]",
                        address.primary
                          ? "border-teal-700 bg-teal-700 text-stone-50"
                          : "border-stone-900/10 bg-white text-stone-600"
                      ].join(" ")}
                    >
                      {address.title}
                    </button>
                  ))}
                </div>

                <form onSubmit={handleAddressSubmit} className="mt-5 grid gap-4">
                  <label className="grid gap-2 text-sm text-stone-600">
                    Название
                    <input
                      value={addressForm.title}
                      onChange={(event) => setAddressForm((current) => ({ ...current, title: event.target.value }))}
                      className="rounded-[1.25rem] border border-teal-900/10 bg-white/84 px-4 py-3 text-base text-stone-950 outline-none transition duration-300 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/10"
                    />
                  </label>

                  <label className="grid gap-2 text-sm text-stone-600">
                    Адрес
                    <input
                      value={addressForm.address}
                      onChange={(event) => setAddressForm((current) => ({ ...current, address: event.target.value }))}
                      className="rounded-[1.25rem] border border-teal-900/10 bg-white/84 px-4 py-3 text-base text-stone-950 outline-none transition duration-300 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/10"
                    />
                  </label>

                  <label className="grid gap-2 text-sm text-stone-600">
                    Детали
                    <textarea
                      value={addressForm.detail}
                      onChange={(event) => setAddressForm((current) => ({ ...current, detail: event.target.value }))}
                      rows={4}
                      className="rounded-[1.4rem] border border-teal-900/10 bg-white/84 px-4 py-3 text-base text-stone-950 outline-none transition duration-300 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/10"
                    />
                  </label>

                  {addressStatus ? <p className="text-sm leading-7 text-emerald-700">{addressStatus}</p> : null}

                  <MagneticButton type="submit" variant="secondary" fullWidth>
                    Сохранить адрес
                  </MagneticButton>
                </form>
              </SectionReveal>

              <SectionReveal delay={0.14} className="rounded-[2.2rem] border border-white/72 bg-white/78 p-6 shadow-[0_34px_78px_-46px_rgba(27,53,52,0.56)] backdrop-blur-2xl">
                <div className="flex items-center gap-3">
                  <CreditCard size={18} className="text-teal-700" />
                  <div>
                    <p className="text-sm uppercase tracking-[0.18em] text-teal-800">Оплата</p>
                    <p className="mt-1 text-sm leading-6 text-stone-600">Выберите основной способ оплаты для следующих заказов.</p>
                  </div>
                </div>

                <div className="mt-5 space-y-3">
                  {paymentMethods.map((method) => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => void setPrimaryPaymentMethod(method.id)}
                      className={[
                        "w-full rounded-[1.5rem] border px-4 py-4 text-left transition duration-300 active:scale-[0.99]",
                        method.primary
                          ? "border-teal-700/20 bg-teal-900/[0.05]"
                          : "border-stone-900/10 bg-stone-50/84"
                      ].join(" ")}
                    >
                      <p className="text-sm font-semibold tracking-tight text-stone-950">{method.label}</p>
                      <p className="mt-1 text-sm text-stone-500">{method.detail}</p>
                    </button>
                  ))}
                </div>

                {primaryPayment ? (
                  <p className="mt-4 text-sm leading-7 text-stone-500">
                    Сейчас по умолчанию: <span className="font-medium text-stone-950">{primaryPayment.label}</span>
                  </p>
                ) : null}
              </SectionReveal>

              <SectionReveal delay={0.18} className="rounded-[2.2rem] border border-white/72 bg-white/78 p-6 shadow-[0_34px_78px_-46px_rgba(27,53,52,0.56)] backdrop-blur-2xl">
                <div className="flex items-center gap-3">
                  <ShieldCheck size={18} className="text-teal-700" />
                  <div>
                    <p className="text-sm uppercase tracking-[0.18em] text-teal-800">Предпочтения</p>
                    <p className="mt-1 text-sm leading-6 text-stone-600">Тонкие настройки, чтобы кухня собирала заказы ближе к вашему привычному сценарию.</p>
                  </div>
                </div>

                <div className="mt-5 space-y-3">
                  {[
                    {
                      key: "removeOnionByDefault",
                      label: "Без лука по умолчанию в шаурме"
                    },
                    {
                      key: "saucesOnSide",
                      label: "Соусы отдельно"
                    },
                    {
                      key: "callInsteadOfIntercom",
                      label: "Звонок вместо домофона"
                    },
                    {
                      key: "halalSeparatePack",
                      label: "Отдельная halal-пометка и упаковка"
                    },
                    {
                      key: "noCutleryByDefault",
                      label: "Не добавлять приборы автоматически"
                    }
                  ].map((item) => {
                    const checked = preferences[item.key as keyof typeof preferences];

                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() =>
                          void savePreferences({
                            ...preferences,
                            [item.key]: !checked
                          })
                        }
                        className={[
                          "flex w-full items-center justify-between rounded-[1.45rem] border px-4 py-3 text-left text-sm transition duration-300 active:scale-[0.99]",
                          checked
                            ? "border-teal-700/20 bg-teal-900/[0.05] text-stone-950"
                            : "border-stone-900/10 bg-stone-50/84 text-stone-600"
                        ].join(" ")}
                      >
                        <span>{item.label}</span>
                        <span className="font-medium">{checked ? "вкл" : "выкл"}</span>
                      </button>
                    );
                  })}
                </div>
              </SectionReveal>
            </div>
          </div>
        </div>
      </section>
      <OrderCancelDialog
        isOpen={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        phone={siteInfo.phone}
        phoneHref={siteInfo.phoneHref}
      />
    </RouteTransition>
  );
}

function StatCard({ title, value }: { title: string; value: string }) {
  return (
    <div className="rounded-[1.6rem] border border-stone-900/8 bg-stone-50/82 p-4">
      <p className="text-[0.72rem] uppercase tracking-[0.16em] text-teal-800">{title}</p>
      <p className="mt-2 text-sm font-semibold tracking-tight text-stone-950">{value}</p>
    </div>
  );
}
